// Node packages
import React from 'react';
import {Grid} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';

// Custom components
import DetailedRecordRow from '../DetailedRecordRow/DetailedRecordRow';
import SubresourcesSection from '../SubresourcesSection';
import RenewalStatusPill from '../RenewalStatusPill/RenewalStatusPill';

// Custom styling and utils
import './DetailedRecordContent.css';
import {subResources} from '../../service/TableProperties';
import {getGlobalSalesIdentifierOptions, getStatusOptionsWithReason} from '../../service/TableService';
import {DetailRecordObject, RecordObject, BenefitObject} from '../../models/ResourceModels';
import {
  customEditThreeYCCommittmentTerms,
  getRecordDetailById,
  createApprovalCode,
  createPA,
  updateResourceStatus,
  updateThreeYCCommittmentTermsWithOnlyAction,
  generateLinkedAccountCode,
  priceLevelRefresh,
  enrollLMMember,
  lmTermsStatus
} from '../../../providers/ResourcesAPI';
import Datepicker from '@react/react-spectrum/Datepicker';
import Select from '@react/react-spectrum/Select';
import Button from '@react/react-spectrum/Button';
import TuneIcon from '@material-ui/icons/Tune';

import {ErrorResponse} from '../../../models/ErrorResponse';
import ErrorDialog from '../../../../src/utils/Dialogs/ErrorDialog';
import InformationDialog from '../../../../src/utils/Dialogs/InformationDialog';
import Edit3YCDetailsDialog from "../../../utils/Dialogs/EditThreeYCDetailsDialog";
import CreatePADialog from "../../../utils/Dialogs/CreatePADialog";
import CreateLeadDialog, { CreateLeadFormData } from "../CreateLeadDialog/CreateLeadDialog";
import { captureLead } from "../../../providers/LeadsAPI";
import {
  getSubSegmentOptions,
  getThreeYCAction
} from "../../../utils/Functions/utils";
import {
  isRcpFlagEnabled,
  isRcpFlagDevEnabled,
  isCreatePaFlagEnabled,
  isCreatePaFlagDevEnabled,
  isWorldWideFlagEnabled,
  isWorldWideFlagDevEnabled,
  isLMflagDevEnabled,
  isLMflagEnabled,
  isTransferLGAFlagEnabled,
  isTransferLGAFlagDevEnabled,
  isFlexPromoFlagDevEnabled,
  isFlexPromoFlagEnabled,
  isOverlayLeadsFlagEnabled,
  isOverlayLeadsFlagDevEnabled,
  isRenewalStatusFlagEnabled,
  isRenewalStatusFlagDevEnabled
} from "../../../config/FeatureFlags";
import ThreeYCAction from "../../../constants/ThreeYCAction";
import ThreeYCStatus from "../../../constants/ThreeYCStatus";
import { forceRefresh as forceRefreshRenewalStatus, notifyOptimisticTrigger } from '../../service/renewalPollerRegistry';

import LinkedMemberEnrollDialog from '../../../utils/Dialogs/LinkedMemberEnrollDialog';
import LinkedMemberTermsDialog from '../../../utils/Dialogs/LinkedMemberTermsDialog';
import PropensityLevelDialog, {PropensitySaveResult} from '../../../utils/Dialogs/PropensityLevelDialog';
import {
  getPropensityPreference,
  setPropensityPreference,
  deletePropensityPreference
} from '../../../providers/PropensityPreferenceAPI';
import PropensityLevel, {PROPENSITY_DEFAULT} from '../../../constants/PropensityLevel';
/**
 * DetailedRecordContent renders the collapsible content of single record row in records table.
 * Contains the detailed information about a record and edit views for the record.
 */

interface DetailedRecordContentProps {
  data: RecordObject;
  // Keeps track of current page tab
  selectedTab: string;
  isCoolDown: boolean;
  handleCoolDown: (isCoolDown: boolean, accountId?: string) => Promise<any>;
  countDownTimer: number;
  handleViewSubresource: (selectedTab: string, searchText: string) => void;
  refreshCurrentTab: (currentTab: string) => void;
  handleRefresh: (callback: (response: DetailRecordObject) => void, response: DetailRecordObject) => void;
}

function DetailedRecordContent(props: DetailedRecordContentProps) {
  const {data, selectedTab, handleViewSubresource, handleRefresh, isCoolDown, handleCoolDown, countDownTimer, refreshCurrentTab} = props;
  const [isEditing, setIsEditing] = React.useState(false);
  const [isCodeOpen, setCodeOpen] = React.useState(false);
  const [isCreatePaOpen, setCreatePaOpen] = React.useState(false);
  const [isCreateLeadOpen, setIsCreateLeadOpen] = React.useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = React.useState(false);
  const [leadSuccessMessage, setLeadSuccessMessage] = React.useState('');
  const [approvalCode, setApprovalCode] = React.useState('');
  const [resellerId, setResellerId] = React.useState('');
  const [linkedAccountCode, setLinkedAccountCode] = React.useState('');
  const [priceLevelMsg, setPriceLevelMsg] = React.useState('');
  const [lmEnrollmentMsg, setLMEnrollmentMsg] = React.useState('');
  const [linkedMemberTermsMsg, setLinkedMemberTermsMsg] = React.useState('');
  const [cotermDate, setCotermDate] = React.useState('');
  const [createdDate, setCreatedDate] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [selectValue, setSelectValue] = React.useState('');
  const [globalSalesEnabled, setGlobalSalesEnabled] = React.useState('false');
  const [marketSegments, setMarketSegments] = React.useState<string[]>([]);
  const [marketSegment, setMarketSegment] = React.useState('');
  const [marketSubSegments, setMarketSubSegments] = React.useState<string[]>([]);
  const [stateData, setStateData] = React.useState<DetailRecordObject | undefined>();
  const [isError, setIsError] = React.useState(false);
  const [errorResponse, setErrorResponse] = React.useState<ErrorResponse | undefined>();
  const [isThreeYCEditing, setIsThreeYCEditing] = React.useState(false);
  const [threeYCAction, setThreeYCAction] = React.useState('');
  const [areRequestedTermsPresent, setAreRequestedTermsPresent] = React.useState(false);
  const [areCommittedTermsPresent, setAreCommittedTermsPresent] = React.useState(false);
  const [isLMEnrollable, setIsLMEnrollable] = React.useState(true);
  const [isLMEnrollableDialogOpen, setIsLMEnrollableDialogOpen] = React.useState(false);
  const [isLMTerms, setIsLMTerms] = React.useState('');
  const [isLMAccepted, setIsLMAccepted] = React.useState(false);
  const [isLMTermsDialogOpen, setIsLMTermsDialogOpen] = React.useState(false);
  const [isPropensityDialogOpen, setIsPropensityDialogOpen] = React.useState(false);
  const [isPropensityLoaded, setIsPropensityLoaded] = React.useState(false);
  const [churnLevel, setChurnLevel] = React.useState<PropensityLevel>(PROPENSITY_DEFAULT);
  const [seatAdditionLevel, setSeatAdditionLevel] = React.useState<PropensityLevel>(PROPENSITY_DEFAULT);

  React.useEffect(() => {
    if (selectedTab === 'customers') {
      getPropensityPreference(data.id).then(response => {
        if (response.httpstatus === 200) {
          setChurnLevel((response.churnLevel as PropensityLevel) || PROPENSITY_DEFAULT);
          setSeatAdditionLevel((response.seatAdditionLevel as PropensityLevel) || PROPENSITY_DEFAULT);
        }
        setIsPropensityLoaded(true);
      }).catch(() => {
        // Still show the control (with default levels) even if the initial fetch fails,
        // e.g. propensity preferences API not reachable in this environment.
        setIsPropensityLoaded(true);
      });
    }
  }, []);
  const [renewalTriggerMessage, setRenewalTriggerMessage] = React.useState('');

  React.useEffect(() => {
    let recordType = selectedTab;
    let recordId = data.id;
    let recordMembershipId = recordType === 'transfers' ? data.membershipId : '';
    let recordAccountId = recordType === 'orders' || recordType === 'retailorders' ? data.accountId : '';
    getRecordDetailById(recordType, recordId, recordMembershipId, recordAccountId).then(
      (response: DetailRecordObject) => {
        if(recordType === 'customers' && response.associations){
          setResellerId(response.associations[0].id);
        }
        if ('isLgaEnabled' in response) {
          delete response.isLgaEnabled;
        }
        setView(response);
      }
    );
  }, []);

  // Normalizes the raw benefits payload into a type-keyed dictionary and derives 3YC LM eligibility.
  const applyBenefitsNormalization = (response: DetailRecordObject) => {
    if (Array.isArray(response.benefits) && response.benefits.length === 0) {
      delete response.benefits;
    }
    if (!("benefits" in response)) {
      return;
    }

    let benefitRead: BenefitObject[] = Array.isArray(response.benefits) ? response.benefits : [];
    let dictionary = Object.assign({}, ...benefitRead.map((b) => ({[b.type]: b})));
    if (dictionary.LARGE_GOVERNMENT_AGENCY) {
      dictionary.LARGE_GOVERNMENT_AGENCY = {
        type: "LARGE_GOVERNMENT_AGENCY",
        status: benefitRead[0].status
      };
    }
    response.benefits = dictionary;

    const ineligibleStatuses = ['REJECTED', 'EXPIRED', 'NONCOMPLIANT', 'DECLINED', 'INACTIVE'];
    if (dictionary.THREE_YEAR_COMMIT) {
      setIsLMEnrollable(ineligibleStatuses.includes(dictionary.THREE_YEAR_COMMIT.commitmentRequest?.status));
    }
  };

  const applyMarketSubSegmentsDefault = (response: DetailRecordObject) => {
    if (response.companyProfile?.marketSegment && !response.companyProfile?.marketSubSegments) {
      response.companyProfile.marketSubSegments = [];
    }
  };

  const isLMIneligibleStatus = (status: string): boolean =>
    ['1004', '1008', '1002', '1022', '1023'].includes(status);

  const stripFlexDiscountsFromLineItems = (response: DetailRecordObject) => {
    if (!("lineItems" in response) || isFlexPromoFlagDevEnabled() || isFlexPromoFlagEnabled() || response.lineItems.length === 0) {
      return;
    }
    response.lineItems.forEach((item) => {
      if (item.flexDiscounts) {
        delete item.flexDiscounts;
      }
    });
  };

  // Flattens the pricing object in lineItems for better display, e.g. partnerPrice, discountedPartnerPrice.
  const flattenLineItemPricing = (response: DetailRecordObject) => {
    if (!("lineItems" in response) || response.lineItems.length === 0) {
      return;
    }
    response.lineItems = response.lineItems.map((item: any) => {
      if (!item.pricing || typeof item.pricing !== 'object') {
        return item;
      }
      const { pricing, ...restOfItem } = item;
      return { ...restOfItem, ...pricing };
    });
  };

  const applyLinkedAccountGroupState = (response: DetailRecordObject) => {
    if (!("linkedAccountGroup" in response)) {
      return;
    }
    if ("isLga" in response.linkedAccountGroup) {
      const isLgaValue = response.linkedAccountGroup.isLga;
      delete response.linkedAccountGroup.isLga;
      if (isLgaValue) {
        (response.linkedAccountGroup as Record<string, any>).benefitsType = ['LARGE_GOVERNMENT_AGENCY'];
      }
    }
    setIsLMEnrollable(false);
    if ("termsAcceptanceStatus" in response.linkedAccountGroup) {
      setIsLMTerms(response.linkedAccountGroup.termsAcceptanceStatus);
    }
    if (response?.linkedAccountGroup?.memberType === 'OWNER' && response?.linkedAccountGroup?.termsAcceptanceStatus === 'ACCEPTED') {
      setIsLMAccepted(true);
    }
  };

  const applyGlobalSalesVisibility = (response: DetailRecordObject) => {
    if (selectedTab === 'customers' && (isWorldWideFlagEnabled() || isWorldWideFlagDevEnabled())) {
      setGlobalSalesEnabled(`${response.globalSalesEnabled}`);
    } else {
      delete response.globalSalesEnabled;
    }
  };

  const pruneLinkedAccountGroupBenefitsForLGA = (response: DetailRecordObject) => {
    if (selectedTab !== 'customers' || isTransferLGAFlagDevEnabled() || isTransferLGAFlagEnabled()) {
      return;
    }
    if ("linkedAccountGroup" in response) {
      if ("benefitsType" in response.linkedAccountGroup) delete response.linkedAccountGroup.benefitsType;
      if ("country" in response.linkedAccountGroup) delete response.linkedAccountGroup.country;
    }
    if ("benefits" in response && response.benefits) {
      if ("LARGE_GOVERNMENT_AGENCY" in response.benefits) delete response.benefits;
    }
  };

  const applyThreeYCState = (response: DetailRecordObject) => {
    if (selectedTab !== 'customers') {
      return;
    }
    if ("benefits" in response) {
      let threeYCBenefits: BenefitObject = (response.benefits as {[id: string]: BenefitObject})['THREE_YEAR_COMMIT'];
      if (threeYCBenefits != null) {
        setAreRequestedTermsPresent("commitmentRequest" in threeYCBenefits);
        setAreCommittedTermsPresent("commitment" in threeYCBenefits);
      }
    }
    setIsThreeYCEditing(false);
    setThreeYCAction(getThreeYCAction(response));
  };

  const setView = (response: DetailRecordObject) => {
    if (response.httpstatus !== 200) {
      setErrorResponse(response);
      setIsLoading(false);
      setIsError(true);
      return;
    }

    applyBenefitsNormalization(response);
    applyMarketSubSegmentsDefault(response);
    if (isLMIneligibleStatus(response.status)) {
      setIsLMEnrollable(false);
    }
    stripFlexDiscountsFromLineItems(response);
    flattenLineItemPricing(response);
    applyLinkedAccountGroupState(response);
    applyGlobalSalesVisibility(response);
    pruneLinkedAccountGroupBenefitsForLGA(response);

    setStateData(response);
    setSelectValue(response.status);
    setMarketSegments(response?.companyProfile?.marketSegments || []);
    setMarketSegment(response?.companyProfile?.marketSegment);
    setMarketSubSegments(response?.companyProfile?.marketSubSegments || []);
    setCotermDate(response.cotermDate);
    setCreatedDate(response.createdDate);
    setIsLoading(false);
    setIsError(false);

    applyThreeYCState(response);
  }

  // Updates view after record is updated. Used as callback function to be Called after records page reloads.
  const updateContent = (response: DetailRecordObject) => {
    setIsEditing(!isEditing);
    setView(response);
  };

  const updateThreeYCContent = (response: DetailRecordObject) => {
    setIsThreeYCEditing(!isThreeYCEditing);
    setView(response)
  }
  const subResourceTypes = subResources[selectedTab];

  const handleEdit = () => {
    let resourceType = selectedTab;
    let resourceId = data.id;
    let accountId = resourceType === 'orders' ? data.accountId : '';

    const recordEdited = selectValue !== '' ||
      marketSegments || 
      marketSegment !== '' ||
      marketSubSegments
    if (isEditing && data && recordEdited) {
      setIsLoading(true);
      updateResourceStatus(
        resourceType,
        resourceId,
        selectValue,
        globalSalesEnabled,
        accountId,
        cotermDate,
        stateData?.cotermDate,
        createdDate,
        marketSegment,
        marketSegments,
        marketSubSegments
      ).then(
        (response: DetailRecordObject) => {
          handleRefresh(updateContent, response);
        }
      );
    } else {
      setIsEditing(!isEditing);
    }
  };

  const handleEditThreeYC = () => {
    let resourceId = data.id;
    if (threeYCAction !== ThreeYCAction.ENROLL_3YC) {
      setIsThreeYCEditing(true);
    } else {
      setIsThreeYCEditing(false);
      setIsLoading(true);
      updateThreeYCCommittmentTermsWithOnlyAction(resourceId, 'ENROLL').then(
        (response: DetailRecordObject) => {
          getRecordDetailById(selectedTab,resourceId,"","").then(
            (response: DetailRecordObject) => {
              handleRefresh(updateThreeYCContent, response);
            }
          )
        }
      );
    }
  }


  const handleCancel = () => {
    setIsEditing(false);
  };

  const handle3YCCancel = () => {
    let resourceId = data.id;
    if (threeYCAction === ThreeYCAction.ACCEPT_3YC_UPDATE || threeYCAction === ThreeYCAction.ACCEPT_3YC_COMMITMENT || threeYCAction === ThreeYCAction.ACCEPT_3YC_RECOMMITMENT) {
      updateThreeYCCommittmentTermsWithOnlyAction(resourceId, 'REJECT').then(
        (response: DetailRecordObject) => {
          getRecordDetailById(selectedTab,resourceId,"","").then(
            (response: DetailRecordObject) => {
              handleRefresh(updateThreeYCContent, response);
            }
          )
        }
      );
    }
    setIsThreeYCEditing(false);
  }

  // Ex. resellers -> Reseller's and retailorders -> Order's
  const getCurrentResourceType = (): string => {
    return selectedTab.startsWith('retail') ?
      selectedTab.charAt(6).toUpperCase() + selectedTab.slice(7, selectedTab.length - 1)
      : selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1, selectedTab.length - 1);
  };

  const handleDismiss3YCDialog = (newThreeYCStatus: string, newStartDate: string, newEndDate: string) => {
    let resourceId = data.id;
    if (isThreeYCEditing) {
      setIsLoading(true);
      let action = '';
      if (threeYCAction === ThreeYCAction.ACCEPT_3YC_COMMITMENT || threeYCAction === ThreeYCAction.ACCEPT_3YC_UPDATE || threeYCAction === ThreeYCAction.ACCEPT_3YC_RECOMMITMENT) {
        action = 'ACCEPT';
      }
      if (threeYCAction === ThreeYCAction.EDIT_3YC_TERMS && newThreeYCStatus === ThreeYCStatus.INACTIVE) {
        action = 'DEACTIVATE';
      }
      if (action !== '') {
        updateThreeYCCommittmentTermsWithOnlyAction(resourceId, action).then(
          (response: DetailRecordObject) => {
            getRecordDetailById(selectedTab,resourceId,"","").then(
              (response: DetailRecordObject) => {
                handleRefresh(updateThreeYCContent, response);
              }
            )
          }
        );
      } else {
        customEditThreeYCCommittmentTerms(resourceId, newStartDate, newEndDate, newThreeYCStatus, areRequestedTermsPresent).then(
          (response: DetailRecordObject) => {
            getRecordDetailById(selectedTab,resourceId,"","").then(
              (response: DetailRecordObject) => {
                handleRefresh(updateThreeYCContent, response);
              }
            )
          }
        );
      }
    } else {
      setIsThreeYCEditing(!isThreeYCEditing);
    }
  }

  const handleDismissErrorDialog = () => {
    if (errorResponse?.httpstatus === 401) {
      sessionStorage.clear();
      window.location.reload();
    } else {
      setIsError(false);
    }
  };

  const handleApprovalCodeClose = () => {
    setApprovalCode('');
    setCodeOpen(false);
  }

  const handleCreatePaClose = () => {
    setCreatePaOpen(false);
  }

  const handleCreateLeadSubmit = (formData: CreateLeadFormData) => {
    const toISOString = (localDateTime: string) =>
      localDateTime ? new Date(localDateTime).toISOString().replace(/\.\d{3}Z$/, 'Z') : undefined;

    const requestBody: any = {
      leadSource: formData.leadSource,
      intent: formData.intent,
      createdAt: toISOString(formData.createdAt),
      expiryDate: toISOString(formData.expiryDate),
      resellerQuoteNeeded: false,
      customer: {
        accountId: formData.customerAccountId || undefined,
        vipId: null
      },
      partner: {
        partnerId: formData.partnerId || undefined,
        partnerName: formData.partnerName || undefined
      },
      source: formData.sourceRequestedBy ? { requestedBy: formData.sourceRequestedBy } : undefined
    };
    setIsSubmittingLead(true);
    captureLead(requestBody).then((response: any) => {
      setIsSubmittingLead(false);
      if (response.httpstatus === 201) {
        setIsCreateLeadOpen(false);
        setLeadSuccessMessage('Lead created successfully!');
      } else {
        setErrorResponse({ httpstatus: response.httpstatus, reason: response.reason || '', message: response.message || 'Failed to create lead. Please try again.' });
        setIsError(true);
      }
    }).catch(() => {
      setIsSubmittingLead(false);
      setErrorResponse({ httpstatus: 500, reason: 'Network Error', message: 'Failed to create lead. Please check your connection.' });
      setIsError(true);
    });
  };

  const renderOverlayLeadsSection = () => {
    if (selectedTab !== 'customers' || !(isOverlayLeadsFlagEnabled() || isOverlayLeadsFlagDevEnabled())) {
      return null;
    }

    return (
      <div>
        <Button variant='action' onClick={() => setIsCreateLeadOpen(true)}>
          Create Lead
        </Button>
        <CreateLeadDialog
          isOpen={isCreateLeadOpen}
          isSubmitting={isSubmittingLead}
          customerAccountId={data.id}
          handleConfirm={handleCreateLeadSubmit}
          handleDismiss={() => setIsCreateLeadOpen(false)}
        />
        <InformationDialog
          isOpen={!!leadSuccessMessage}
          title="Lead Created"
          message={leadSuccessMessage}
          variant="confirmation"
          handleConfirmConfirmationDialog={() => setLeadSuccessMessage('')}
        />
      </div>
    );
  };

  const renderEditControls = () => {
    return (
      <>
        {!isThreeYCEditing ? <Button variant='action' onClick={handleEdit}>
          {isEditing ? 'Save' : 'Edit'}
        </Button> : null}
        {isEditing && (
          <Button variant='action' onClick={handleCancel}>
            Cancel
          </Button>
        )}
      </>
    );
  };

  const renderTriggerAutoRenewalButton = () => {
    const isEligibleTab = selectedTab === 'customers' || (selectedTab === 'retailorders' && selectValue === '1000');
    if (!isEligibleTab || isEditing || isThreeYCEditing) {
      return null;
    }
    // isCoolDown/countDownTimer only gate re-triggering — the adjacent RenewalStatusPill
    // (not this button) is the source of truth for whether a renewal is actually in flight.
    return (
      <Button variant='action' disabled={isCoolDown} onClick={triggerAutoRenewal}>
        Trigger Auto Renewal
        ({countDownTimer})
      </Button>
    );
  };

  const renderThreeYCActionButton = () => {
    if (selectedTab !== 'customers' || isEditing || isThreeYCEditing || selectValue !== '1000' || threeYCAction === '') {
      return null;
    }
    return (
      <Button variant='action' onClick={handleEditThreeYC}>
        {threeYCAction}
      </Button>
    );
  };

  const renderRcpSection = () => {
    if (selectedTab !== 'customers' || !(isRcpFlagEnabled() || isRcpFlagDevEnabled())) {
      return null;
    }
    return (
      <div>
        <Button variant='action' onClick={generateApprovalCode}>
          Generate Approval Code
        </Button>
        <InformationDialog
          isOpen={isCodeOpen}
          title={'Approval Code'}
          message={approvalCode}
          variant={'information'}
          handleConfirmConfirmationDialog={handleApprovalCodeClose}
        />
      </div>
    );
  };

  const renderCreatePaSection = () => {
    if (selectedTab !== 'customers' || !(isCreatePaFlagEnabled() || isCreatePaFlagDevEnabled())) {
      return null;
    }
    return (
      <div>
        <Button variant='action' onClick={createPAforCustomer}>
          Create PA
        </Button>
        <CreatePADialog
          isOpen={isCreatePaOpen}
          customerId={data.id}
          resellerId={resellerId}
          isChinaDistributor={stateData?.companyProfile?.address?.country === 'CN'}
          handleConfirmConfirmationDialog={handleCreatePaSubmit}
          handleDismissConfirmationDialog={handleCreatePaClose}
        />
      </div>
    );
  };

  const renderPropensitySection = () => {
    if (selectedTab !== 'customers' || !isPropensityLoaded) {
      return null;
    }
    return (
      <div>
        <Button variant='action' icon={<TuneIcon />} onClick={() => setIsPropensityDialogOpen(true)}>
          Set Propensity Level
        </Button>
        <PropensityLevelDialog
          isOpen={isPropensityDialogOpen}
          customerId={data.id}
          churnLevel={churnLevel}
          seatAdditionLevel={seatAdditionLevel}
          handleSave={handlePropensitySave}
          handleDismiss={() => setIsPropensityDialogOpen(false)}
          handleResetToDefault={handlePropensityReset}
        />
      </div>
    );
  };

  const renderLinkedMembershipEnrollSection = () => {
    if (selectedTab !== 'customers' || !(isLMflagDevEnabled() || isLMflagEnabled()) || !isLMEnrollable) {
      return null;
    }
    return (
      <>
        <Button variant='action' onClick={() => setIsLMEnrollableDialogOpen(!isLMEnrollableDialogOpen)}>
          Enroll to Linked Membership
        </Button>
        <LinkedMemberEnrollDialog
          isOpen={isLMEnrollableDialogOpen}
          customerId={data.id}
          title='Linked Member Enrollment'
          variant='confirmation'
          handleLinkedMemberEnrollDialog={handleLMEnrollments}
          handleLMDismissConfirmationDialog={() => setIsLMEnrollableDialogOpen(!isLMEnrollableDialogOpen)}
        />
      </>
    );
  };

  const renderLinkedMembershipTermsSection = () => {
    if (selectedTab !== 'customers' || !(isLMflagDevEnabled() || isLMflagEnabled()) || isLMTerms !== 'REQUESTED') {
      return null;
    }
    return (
      <>
        <Button variant='action' onClick={() => setIsLMTermsDialogOpen(!isLMTermsDialogOpen)}>
          Linked Membership Terms
        </Button>
        <LinkedMemberTermsDialog
          isOpen={isLMTermsDialogOpen}
          customerId={data.id}
          variant='confirmation'
          handleLinkedMemberTermsSubmit={handleLinkedMemberTermsSubmit}
        />
      </>
    );
  };

  const renderRenewalStatusPill = () => {
    if (selectedTab !== 'customers' || !(isRenewalStatusFlagEnabled() || isRenewalStatusFlagDevEnabled())) {
      return null;
    }
    return <RenewalStatusPill accountId={data.id} />;
  };

  const getMarketSegmentOptions = () => {
    return [
      {
        label: 'COM',
        value: 'COM'
      },
      {
        label: 'GOV',
        value: 'GOV'
      },
      {
        label: 'EDU',
        value: 'EDU'
      },
    ]
  }


  // Render view of cells to edit object values
  const renderEditCells = (key: string, val: string | number | object) => {
    if (isEditing && key === 'creationDate' && selectedTab === 'orders') {
      return (
        <Datepicker
          value={createdDate}
          onChange={value => {
            if (typeof value === 'string') {
              setCreatedDate(value);
            }
          }}
          selectionType='single'
          type='date'
        />
      );
    } else if (isEditing && key === 'status') {
      return (
        <div className='table-cell'>
          <Select
            aria-label='Status Code'
            value={selectValue}
            options={getStatusOptionsWithReason(selectedTab, selectValue)}
            onSelect={value => setSelectValue(value)}
          />
        </div>
      );
    } else if (isEditing && key === 'globalSalesEnabled' && selectedTab === 'customers') {
      return (
        <div className='table-cell'>
          <Select
            aria-label='Global Sales Enabled'
            value={`${globalSalesEnabled}`}
            options={getGlobalSalesIdentifierOptions()}
            onSelect={value => setGlobalSalesEnabled(value)}
          />
        </div>
      );
    } else if (isEditing && key === 'cotermDate' && selectedTab === 'customers') {
      return (
        <Datepicker
          value={cotermDate}
          onChange={value => {
            if (typeof value === 'string') {
              setCotermDate(value);
            }
          }}
          min='today'
          selectionType='single'
          type='date'
        />
      );
    } else if (isEditing && key === 'marketSegments') {
      return (
        <div className='table-cell'>
          <Select
            multiple
            aria-label='Market segments'
            placeholder="Market Segments"
            value={marketSegments}
            options={getMarketSegmentOptions()}
            onSelect={value => {
              const selectedMarketSegments = value as unknown
              setMarketSegments(selectedMarketSegments as string[])
            }}
          />
        </div>
      ); 
    } else if (isEditing && key === 'marketSegment') {
      return (
        <div className='table-cell'>
          <Select
            aria-label='Market segment'
            placeholder="Market Segment"
            value={marketSegment}
            options={getMarketSegmentOptions()}
            onSelect={value => {
              setMarketSegment(value)
              setMarketSubSegments([])
            }}
          />
        </div>
      ); 
    } else if (isEditing && key === 'marketSubSegments' && marketSegment === 'EDU') {
      return (
        <div className='table-cell'>
          <Select
            multiple
            aria-label='Sub Segments'
            placeholder="Sub Segments"
            value={marketSubSegments}
            options={getSubSegmentOptions(marketSegment, marketSubSegments)}
            onSelect={value => {
              const selectedMarketSubSegments = value as unknown
              setMarketSubSegments(selectedMarketSubSegments as string[])
            }}
          />
        </div>
      );
    } else if ((isTransferLGAFlagEnabled() || isTransferLGAFlagDevEnabled()) &&
            isEditing && key === 'marketSubSegments' && marketSegment === 'GOV') {
      return (
          <div className='table-cell'>
            <Select
                multiple
                aria-label='Sub Segments'
                placeholder="Sub Segments"
                value={marketSubSegments}
                options={getSubSegmentOptions(marketSegment, marketSubSegments)}
                onSelect={value => {
                  const selectedMarketSubSegments = value as unknown
                  setMarketSubSegments(selectedMarketSubSegments as string[])
                }}
            />
          </div>
      );
    } else {
      return null;
    }
  };

  const renderThree3YCEdit = () => {
    if (isThreeYCEditing && threeYCAction !== ThreeYCAction.ENROLL_3YC && selectedTab === 'customers') {
      return <Edit3YCDetailsDialog
        isThreeYCEditing={isThreeYCEditing}
        response={stateData}
        threeYCAction={threeYCAction}
        areCommittedTermsPresent={areCommittedTermsPresent}
        areRequestedTermsPresent={areRequestedTermsPresent}
        handleDismiss3YCDialog={handleDismiss3YCDialog}
        handleDismiss3YCCancelDialog={handle3YCCancel}/>
    } else {
      return null;
    }
  }

  const handleLinkedAccountCode = async () => {
    const customerId = data.id;
    const linkedId = stateData?.linkedAccountGroup?.id
    const res = await generateLinkedAccountCode(linkedId, customerId);
    setLinkedAccountCode(res?.approvalCode ? res.approvalCode : (res?.message || 'Failed to generate linked membership code'));
  }

  const handlePriceLevel = async () => {
    const customerId = data.id;
    const linkedId = stateData?.linkedAccountGroup?.id
    const res = await priceLevelRefresh(linkedId, customerId);
    setPriceLevelMsg(res?.message || 'Request to refresh discount level submitted successfully');
  }

  function triggerAutoRenewal() {
    handleCoolDown(true, selectedTab === 'customers' ? data.id : undefined).then((response: any) => {
      if (selectedTab === 'customers') {
        if (response?.httpstatus >= 200 && response?.httpstatus < 300) {
          // Flip the pill to "triggered" immediately rather than waiting for the next
          // poll tick, then reconcile with the real polled status right away.
          notifyOptimisticTrigger(data.id);
        } else {
          // A 409 means a live attempt already exists — force an immediate refetch so
          // the pill reflects its real (possibly already in-progress) state right away,
          // rather than injecting a fake "triggered" event for an attempt we didn't start.
          forceRefreshRenewalStatus(data.id);
        }
      }
      if (response?.httpstatus === 409) {
        setRenewalTriggerMessage('A renewal is already in progress for this account.');
      }
    });
  }

  const generateApprovalCode = () => {
    let resourceId = data.id;
    createApprovalCode(resourceId).then(
      (response: string) => {
         setApprovalCode(response);
      }
    );
    setCodeOpen(true);
  }

  const handleCreatePaSubmit = (customerId : string, resellerId : string, productValue : string, productQty : number) => {
    createPA(customerId, resellerId, productValue, productQty).then(
      (response: string) => {
        setCreatePaOpen(false);
      }
    );
  }

  const createPAforCustomer = () => {
    let resourceId = data.id;
    console.log("Create PA for customer with id: " + resourceId);
    setCreatePaOpen(true);
  }

  const displaySubresourcesSection = (recordVal: string) =>
    recordVal === 'deployments'
      ? stateData?.globalSalesEnabled
      : true
  
  const handleLMEnrollments = async (customerId: string, approvalCode: string) => {
    const res = await enrollLMMember(customerId, approvalCode);
    setIsLMEnrollableDialogOpen(false);
    setLMEnrollmentMsg(res?.message || 'Successfully Enrolled');
  }

  const handleLinkedMemberTermsSubmit = async (status: string) => {
    const customerId = data.id;
    const res = await lmTermsStatus(customerId, status)

    setIsLMTermsDialogOpen(false);
    setLinkedMemberTermsMsg(res || 'Successfully Enrolled');
  }

  const handlePropensitySave = (
    newChurnLevel: PropensityLevel | undefined,
    newSeatAdditionLevel: PropensityLevel | undefined
  ): Promise<PropensitySaveResult> => {
    // Every failure path here — 4xx, 5xx, or a network/CORS-level fetch rejection — resolves to
    // an inline failure inside PropensityLevelDialog rather than the app-wide ErrorDialog. That
    // dialog replaces this entire customer detail panel, which is too disruptive for what is
    // a small, contained, easily-retried action; keep the failure local to where it happened.
    return setPropensityPreference(data.id, newChurnLevel, newSeatAdditionLevel).then(response => {
      if (response.httpstatus === 200) {
        if (response.churnLevel) setChurnLevel(response.churnLevel as PropensityLevel);
        if (response.seatAdditionLevel) setSeatAdditionLevel(response.seatAdditionLevel as PropensityLevel);
        return {success: true};
      }
      return {success: false, message: response.message || `Failed to set propensity level (HTTP ${response.httpstatus}).`};
    }).catch(() => {
      // fetch() rejects (rather than resolving with a response) for CORS-blocked and true
      // network failures alike — the browser never exposes a real status code for either case.
      return {success: false, message: 'Could not reach the server. Check your connection and try again.'};
    });
  }

  const handlePropensityReset = (): Promise<PropensitySaveResult> => {
    return deletePropensityPreference(data.id).then(response => {
      if (response.httpstatus === 204 || response.httpstatus === 200) {
        // Re-fetch rather than assuming a default client-side — the resolved default lives on
        // the backend, so read it back and reflect whatever it actually reset to. If this
        // re-fetch itself fails, the reset technically succeeded server-side but we can no
        // longer confirm what it resolved to, so report failure rather than showing a stale
        // "Done" with unrefreshed levels.
        return getPropensityPreference(data.id).then(getResponse => {
          if (getResponse.httpstatus !== 200) {
            return {success: false, message: getResponse.message || `Reset succeeded but could not confirm the new levels (HTTP ${getResponse.httpstatus}).`};
          }
          if (getResponse.churnLevel) setChurnLevel(getResponse.churnLevel as PropensityLevel);
          if (getResponse.seatAdditionLevel) setSeatAdditionLevel(getResponse.seatAdditionLevel as PropensityLevel);
          return {success: true};
        });
      }
      return {success: false, message: response.message || `Failed to reset propensity level (HTTP ${response.httpstatus}).`};
    }).catch(() => {
      return {success: false, message: 'Could not reach the server. Check your connection and try again.'};
    });
  }

  const renderContent = () => {
    return (
      <div>
        <Grid container spacing={5}>
          <Grid item xs={10}>
            <div className='table'>
              <div className='table-header'>
                <div className='table-row'>
                  <div className='table-small-cell'/>
                  <div className='table-cell'>Field</div>
                  <div className='table-cell'>Value</div>
                  {isEditing && <div className='table-cell'>Update Value</div>}
                </div>
              </div>
              <div className='table-body'>
                {stateData &&
                  Object.entries(stateData).map(([recordKey, recordVal]) => {
                    if (recordKey === 'httpstatus' || recordKey === 'message' || recordKey === 'reason' || recordVal === null
                    ) {
                    } else {
                      return (
                        <>
                          <DetailedRecordRow
                            key={recordKey}
                            rowKey={recordKey}
                            rowVal={recordVal}
                            editable={true}
                            isEditing={isEditing}
                            renderEdit={renderEditCells}
                            renderThreeYCEdit={renderThree3YCEdit}
                            handleLinkedAccountCode={handleLinkedAccountCode}
                            handlePriceLevel={handlePriceLevel}
                            isLMAccepted={isLMAccepted}
                          />
                          {Boolean(linkedAccountCode || priceLevelMsg || lmEnrollmentMsg || linkedMemberTermsMsg) && (
                            <InformationDialog
                              isOpen={true}
                              title={linkedAccountCode ? 'Linked Membership Code' : ''}
                              message={linkedAccountCode || priceLevelMsg || lmEnrollmentMsg || linkedMemberTermsMsg}
                              variant={'information'}
                              className={lmEnrollmentMsg ? 'information-dialog-msg' : ''}
                              handleConfirmConfirmationDialog={() => {
                                setLinkedAccountCode('')
                                setPriceLevelMsg('')
                                setLMEnrollmentMsg('')

                                if (linkedMemberTermsMsg || lmEnrollmentMsg) {
                                  refreshCurrentTab('customers');
                                }
                                setLinkedMemberTermsMsg('')
                              }}
                            />
                          )}
                        </>
                      )
                    }
                  })}
              </div>
            </div>
          </Grid>
          <Grid item xs={2}>
            {renderEditControls()}
            <br/>
            {renderTriggerAutoRenewalButton()}
            <br/>
            {renderRenewalStatusPill()}
            <br/>
            {renderThreeYCActionButton()}
            <br/>
            {renderRcpSection()}
            <br/>
            {renderCreatePaSection()}
            <br/>
            {renderPropensitySection()}
            <br />
            {renderOverlayLeadsSection()}
            <br />
            {renderLinkedMembershipEnrollSection()}
            {renderLinkedMembershipTermsSection()}
          </Grid>
        </Grid>
        <InformationDialog
          isOpen={!!renewalTriggerMessage}
          title='Renewal In Progress'
          message={renewalTriggerMessage}
          variant='information'
          handleConfirmConfirmationDialog={() => setRenewalTriggerMessage('')}
        />
        {Object.entries(subResourceTypes).map(([recordKey, recordVal]) => {
          let currentRecordVal = selectedTab.startsWith('retail') ? recordVal.charAt(6).toUpperCase() + recordVal.slice(7)
            : recordVal.charAt(0).toUpperCase() + recordVal.slice(1);
          return (
            <>
              {displaySubresourcesSection(recordVal) && 
                <SubresourcesSection
                  key={recordKey}
                  title={`${getCurrentResourceType()}'s Associated ${currentRecordVal}`}
                  subresourceType={recordVal}
                  handleViewSubresource={handleViewSubresource}
                  resourceType={selectedTab}
                  resourceId={data.id}
                />
              }
            </>
        )})}
      </div>
    );
  };

  return (
    <div> {isLoading ? (<CircularProgress className='table-loading-spinner' size={20}/>) : [
      (errorResponse && isError ?
        <ErrorDialog
          isError={true}
          status={errorResponse.httpstatus}
          reason={errorResponse.reason}
          message={errorResponse.message}
          handleDismissErrorDialog={handleDismissErrorDialog}
        />
        :
        renderContent())
    ]
    }
    </div>
  )
}

export default DetailedRecordContent;
